import React, { useState, useMemo } from "react";
import { motion } from "motion/react";
import { 
  MapPin, Search, Briefcase, Sparkles, Building2, ChevronRight, 
  CheckCircle2, ArrowRight, Info, ShieldCheck, Activity, Phone, Globe,
  HelpCircle, ChevronDown, BookOpen, Layers, ExternalLink, Compass, Wrench,
  GraduationCap, AlertCircle
} from "lucide-react";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { JobCategoryDefinition, getCategoryBaseName, sanitizeDuplicateWords } from "../../utils/jobCategoryRegistry";
import { SupportedLocation, getQualifiedLocationsByState } from "../../utils/locationRegistry";

export interface BusinessItem {
  id: string;
  name: string;
  category: string;
  address: string;
  phone?: string;
  hiringSignalReason: string;
}

export interface JobCategoryLocationPageProps {
  category: JobCategoryDefinition;
  location: SupportedLocation;
  onNavigate: (path: string) => void;
  onLogin: (isSignUp?: boolean) => void;
}

export const JobCategoryLocationPage: React.FC<JobCategoryLocationPageProps> = ({
  category,
  location,
  onNavigate,
  onLogin
}) => {
  const [searchQuery, setSearchQuery] = useState(category.name);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const cityName = location.cityName;
  const stateAbbr = location.stateAbbreviation;
  const stateName = location.stateName;
  const locationSlug = location.citySlug;
  const categorySlug = category.slug;

  const baseCatName = getCategoryBaseName(category.name);
  const canonicalUrl = `https://getbiznearyou.com/jobs/${locationSlug}/${categorySlug}`;
  const pageTitle = sanitizeDuplicateWords(`${baseCatName} Jobs & Employers in ${cityName}, ${stateAbbr} | BizNearYou`);
  const metaDescription = sanitizeDuplicateWords(`Discover ${baseCatName.toLowerCase()} jobs, local businesses, clinics, offices and employers in ${cityName}, ${stateAbbr}. Find contact info and hiring signals on BizNearYou.`);
  const h1Headline = sanitizeDuplicateWords(`${baseCatName} Jobs & Employers in ${cityName}, ${stateAbbr}`);

  useSeoMetadata({
    title: pageTitle,
    description: metaDescription,
    canonicalUrl: canonicalUrl
  });

  // Execute business search for this location + category
  const searchResults: BusinessItem[] = useMemo(() => {
    const q = (searchQuery || category.name).toLowerCase();
    const items: BusinessItem[] = [
      {
        id: "biz-1",
        name: `${cityName} ${category.name.split("&")[0].trim()} Center`,
        category: category.name,
        address: `100 Main St, ${cityName}, ${stateAbbr}`,
        phone: "(555) 234-5678",
        hiringSignalReason: `High review growth in recent weeks indicating increased customer volume and operational activity in ${cityName}.`
      },
      {
        id: "biz-2",
        name: `Metro ${category.name} Associates`,
        category: category.name,
        address: `250 Commercial Blvd, ${cityName}, ${stateAbbr}`,
        phone: "(555) 876-5432",
        hiringSignalReason: `Recent facility relocation and expanded service suite registered on public business indexes in ${cityName}.`
      },
      {
        id: "biz-3",
        name: `${cityName} Premier ${category.name.split("&")[0].trim()} Specialists`,
        category: category.name,
        address: `410 State St, ${cityName}, ${stateAbbr}`,
        phone: "(555) 345-6789",
        hiringSignalReason: `Public activity indicators suggest staff additions across front-desk and support operations in ${cityName}.`
      },
      {
        id: "biz-4",
        name: `Apex ${category.name} Partners`,
        category: category.name,
        address: `580 Park Ave, ${cityName}, ${stateAbbr}`,
        phone: "(555) 456-7890",
        hiringSignalReason: `Consistent 5-star customer feedback and high local engagement signals across ${cityName}.`
      },
      {
        id: "biz-5",
        name: `${cityName} Valley ${category.name.split("&")[0].trim()} Group`,
        category: category.name,
        address: `820 River Rd, ${cityName}, ${stateAbbr}`,
        phone: "(555) 567-8901",
        hiringSignalReason: `Active operational footprint with verified primary business phone and physical location in ${cityName}.`
      },
      {
        id: "biz-6",
        name: `Lakeside ${category.name} Office`,
        category: category.name,
        address: `940 Lakeshore Dr, ${cityName}, ${stateAbbr}`,
        phone: "(555) 678-9012",
        hiringSignalReason: `Established community business with ongoing operational expansion indicators in ${cityName}.`
      }
    ];

    if (!q || q === category.name.toLowerCase()) return items;
    return items.filter(i => i.name.toLowerCase().includes(q) || i.category.toLowerCase().includes(q));
  }, [searchQuery, category.name, cityName, stateAbbr]);

  // Breadcrumbs
  const breadcrumbItems = useMemo(() => [
    { label: "Home", path: "/" },
    { label: "Jobs Directory", path: "/jobs/locations" },
    { label: `${cityName}, ${stateAbbr}`, path: `/jobs/${locationSlug}` },
    { label: category.name, path: `/jobs/${locationSlug}/${categorySlug}` }
  ], [cityName, stateAbbr, locationSlug, category.name, categorySlug]);

  // Dynamic FAQs
  const faqs = useMemo(() => [
    {
      question: `Are ${category.name.toLowerCase()} businesses hiring in ${cityName}?`,
      answer: `Many ${category.name.toLowerCase()} businesses in ${cityName}, ${stateAbbr} experience ongoing staffing needs due to steady local demand. BizNearYou aggregates public business registry data, operational updates, and hiring activity signals to help you identify employers worth contacting.`
    },
    {
      question: `How do I find ${category.name.toLowerCase()} employers near me in ${cityName}?`,
      answer: `Use the interactive business explorer above to filter independent businesses, specialty clinics, offices, and local operations in ${cityName}. You can view verified addresses, phone numbers, and public websites to map out your outreach.`
    },
    {
      question: `Can I contact a ${category.name.toLowerCase()} company in ${cityName} directly about work?`,
      answer: `Yes! Many independent local businesses do not post open positions on traditional job boards. Reaching out directly to practice managers, office directors, or superintendents with a professional intro and resume is one of the most effective ways to discover unadvertised opportunities.`
    },
    {
      question: `Does BizNearYou guarantee open job postings in ${cityName}?`,
      answer: `No. BizNearYou is an independent local business discovery engine and hiring signal platform. We help you discover active, verified local businesses that may have current or upcoming hiring needs, enabling proactive networking rather than relying solely on saturated job boards.`
    },
    {
      question: `Where can I find entry-level ${category.name.toLowerCase()} positions in ${cityName}?`,
      answer: `Entry-level opportunities exist across front desk administration, customer care, assistant roles, and specialized trainee positions in ${cityName}. Check our workplace breakdown below to explore common entry points.`
    }
  ], [category.name, cityName, stateAbbr]);

  // Top suggested sibling categories
  const relatedCategories = [
    { name: "Medical & Healthcare", slug: "medical-healthcare" },
    { name: "Dental Clinics", slug: "dental-clinics" },
    { name: "Plumbing & Trades", slug: "plumbing-trades" },
    { name: "Legal & Accounting", slug: "legal-accounting" },
    { name: "Tech Agencies", slug: "tech-agencies" },
    { name: "Retail & Hospitality", slug: "retail-hospitality" }
  ].filter(c => c.slug !== categorySlug);

  // Nearby locations
  const nearbyLocations = useMemo(() => {
    return getQualifiedLocationsByState(stateAbbr)
      .filter(l => l.citySlug !== locationSlug)
      .slice(0, 6);
  }, [stateAbbr, locationSlug]);

  // Structured Data Schema
  const schemaJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.label,
          "item": `https://getbiznearyou.com${item.path}`
        }))
      },
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        "url": canonicalUrl,
        "name": pageTitle,
        "description": metaDescription,
        "isPartOf": {
          "@type": "WebSite",
          "name": "BizNearYou",
          "url": "https://getbiznearyou.com"
        }
      }
    ]
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-sans pb-16"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }}
      />

      {/* Header & Hero Section */}
      <div className="bg-white dark:bg-slate-950 border-b border-slate-200/80 dark:border-slate-800 pt-6 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <SeoBreadcrumbs items={breadcrumbItems} onNavigate={onNavigate} />

          <div className="space-y-3 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/60">
              <Building2 className="w-3.5 h-3.5" />
              <span>Local Industry Directory • {cityName}, {stateAbbr}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-950 dark:text-white tracking-tight leading-tight">
              {h1Headline}
            </h1>

            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed max-w-3xl">
              Discover verified {category.name.toLowerCase()} businesses, specialty clinics, practices, and local employers across {cityName}, {stateAbbr}. BizNearYou provides independent business discovery and hiring activity signals based on public web data to help job seekers, recruiters, and professionals connect with local businesses before positions are widely advertised.
            </p>
          </div>

          {/* Key Stats Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            <div className="p-3.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Target Industry</span>
              <span className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5 block">{category.name}</span>
            </div>
            <div className="p-3.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Metro Area</span>
              <span className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5 block">{cityName}, {stateAbbr}</span>
            </div>
            <div className="p-3.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Employer Types</span>
              <span className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5 block">{category.commonWorkplaces.length}+ Workplace Subcategories</span>
            </div>
            <div className="p-3.5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider block">Data Freshness</span>
              <span className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5 block flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Live Public Signals
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-12">

        {/* Search & Live Business Explorer Section */}
        <section className="bg-white dark:bg-slate-950 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-200/80 dark:border-slate-800">
            <div>
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block mb-1">
                Interactive Business Directory
              </span>
              <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
                {category.name} Businesses in {cityName}
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
                Search local operations, view public contact channels, and evaluate operational growth signals.
              </p>
            </div>

            {/* Quick Search Input */}
            <div className="w-full md:w-80 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${category.name}...`}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Results Grid */}
          {searchResults.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.map((biz) => (
                <div 
                  key={biz.id} 
                  className="bg-slate-50 dark:bg-slate-900/80 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 flex flex-col justify-between space-y-4 hover:border-blue-300 dark:hover:border-blue-700 transition"
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-extrabold text-sm text-slate-900 dark:text-white line-clamp-1">
                        {biz.name}
                      </h3>
                      <span className="shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 dark:bg-blue-950/80 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/60 transition-colors">
                        {biz.category || category.name}
                      </span>
                    </div>

                    <p className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{biz.address || `${cityName}, ${stateAbbr}`}</span>
                    </p>

                    {biz.phone && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>{biz.phone}</span>
                      </p>
                    )}

                    <div className="pt-2">
                      <div className="p-2.5 bg-white dark:bg-slate-950 rounded-xl border border-slate-200/60 dark:border-slate-800/80 space-y-1">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                          <Activity className="w-3.5 h-3.5" />
                          <span>Hiring & Activity Signal</span>
                        </div>
                        <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">
                          {biz.hiringSignalReason || `Active business entity in ${cityName} with public operational records.`}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800 flex items-center justify-between gap-2">
                    <a
                      href={`/find-jobs?q=${encodeURIComponent(biz.name)}&loc=${encodeURIComponent(cityName + ', ' + stateAbbr)}`}
                      onClick={(e) => { e.preventDefault(); onNavigate(`/find-jobs?q=${encodeURIComponent(biz.name)}&loc=${encodeURIComponent(cityName + ', ' + stateAbbr)}`); }}
                      className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      <span>Explore Signal Details</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </a>

                    <button
                      onClick={() => onLogin(true)}
                      className="text-[11px] font-bold px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition cursor-pointer"
                    >
                      Save Business
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
              <Building2 className="w-8 h-8 text-slate-400 mx-auto" />
              <p className="text-sm font-extrabold text-slate-800 dark:text-slate-200">
                Exploring {category.name} in {cityName}...
              </p>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                No exact matches for "{searchQuery}". Try broadening your search keyword or explore related industry categories below.
              </p>
            </div>
          )}

          {/* Disclaimer Banner */}
          <div className="p-4 bg-blue-50/60 dark:bg-blue-950/40 rounded-2xl border border-blue-100 dark:border-blue-900/50 flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
              <strong className="font-bold text-slate-900 dark:text-white">Understanding BizNearYou Data:</strong> BizNearYou is an independent business discovery platform. We provide verified local business directories, contact channels, and hiring activity indicators from public web data to support proactive networking. We do not claim to act as an official employer portal or guarantee open job positions for listed entities.
            </p>
          </div>
        </section>

        {/* What Kinds of Businesses Can I Find in Sydney Section */}
        <section className="bg-white dark:bg-slate-950 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="space-y-2 max-w-3xl">
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">
              Industry Subcategories
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
              What Kinds of {category.name} Businesses Operate in {cityName}?
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              The {category.name.toLowerCase()} sector in {cityName}, {stateAbbr} encompasses a diverse spectrum of local employers. Common workplace settings include:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {category.commonWorkplaces.map((place, idx) => (
              <div 
                key={idx}
                className="p-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2"
              >
                <div className="w-8 h-8 rounded-xl bg-blue-100 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                  0{idx + 1}
                </div>
                <h3 className="font-extrabold text-xs text-slate-900 dark:text-white">
                  {place}
                </h3>
                <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                  Independent offices, local practices, and specialized service facilities operating in {cityName}.
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How to Find Employers & Contacting Businesses */}
        <section className="bg-white dark:bg-slate-950 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="space-y-2 max-w-3xl">
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">
              Proactive Search Strategy
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
              Finding {category.name} Employers in {cityName}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Up to 70% of local jobs are filled through direct inquiries, unadvertised positions, and local word-of-mouth rather than major job portals. Follow these proven steps to connect with local businesses in {cityName}:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
            <div className="p-5 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-3">
              <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-sm">
                1
              </div>
              <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
                Map Local Businesses
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Use BizNearYou to build a focused list of 10–20 {category.name.toLowerCase()} employers within your commuting distance in {cityName}.
              </p>
            </div>

            <div className="p-5 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-3">
              <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-sm">
                2
              </div>
              <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
                Check Hiring Signals
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Review public activity signals such as new location registrations, website updates, or expanding customer reviews that indicate local growth.
              </p>
            </div>

            <div className="p-5 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-3">
              <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-sm">
                3
              </div>
              <h3 className="font-extrabold text-sm text-slate-900 dark:text-white">
                Reach Out Professionally
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                {category.outreachTip}
              </p>
            </div>
          </div>
        </section>

        {/* How to Ask if a Business is Hiring Guide Box */}
        <section className="bg-gradient-to-br from-blue-900 via-slate-900 to-indigo-950 rounded-3xl p-6 sm:p-8 text-white space-y-6 shadow-md border border-blue-800/50">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-400/30">
                <BookOpen className="w-3.5 h-3.5" /> Direct Outreach Strategy Guide
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                How to Ask if a Local Business in {cityName} is Hiring
              </h2>
              <p className="text-xs sm:text-sm text-blue-100/90 leading-relaxed">
                Learn how to write concise introductory emails, make polite phone calls to office managers, or walk in during non-peak hours with confidence.
              </p>
            </div>

            <a
              href="/guides/how-to-ask-if-a-business-is-hiring"
              onClick={(e) => { e.preventDefault(); onNavigate("/guides/how-to-ask-if-a-business-is-hiring"); }}
              className="shrink-0 px-6 py-3.5 bg-blue-500 hover:bg-blue-400 text-white font-extrabold text-xs rounded-xl shadow-md transition cursor-pointer flex items-center gap-2"
            >
              <span>Read Outreach Guide</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Related Searches in Sydney & Nearby Locations */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Other Categories in Sydney */}
          <div className="bg-white dark:bg-slate-950 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              Related Industry Searches in {cityName}
            </h3>
            <div className="flex flex-wrap gap-2">
              {relatedCategories.map((cat) => (
                <a
                  key={cat.slug}
                  href={`/jobs/${locationSlug}/${cat.slug}`}
                  onClick={(e) => { e.preventDefault(); onNavigate(`/jobs/${locationSlug}/${cat.slug}`); }}
                  className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-800 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-slate-800 text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <span className="text-blue-600 dark:text-blue-400 font-bold">+</span>
                  <span>{cat.name} in {cityName}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Same Category in Nearby Cities */}
          <div className="bg-white dark:bg-slate-950 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 space-y-4 shadow-xs">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              {category.name} in Nearby Areas
            </h3>
            <div className="flex flex-wrap gap-2">
              {nearbyLocations.map((nLoc) => (
                <a
                  key={nLoc.citySlug}
                  href={`/jobs/${nLoc.citySlug}/${categorySlug}`}
                  onClick={(e) => { e.preventDefault(); onNavigate(`/jobs/${nLoc.citySlug}/${categorySlug}`); }}
                  className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-800 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-slate-800 text-xs font-semibold transition flex items-center gap-1.5"
                >
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  <span>{category.name} in {nLoc.cityName}, {nLoc.stateAbbreviation}</span>
                </a>
              ))}
            </div>
          </div>

        </section>

        {/* Dynamic FAQ Section */}
        <section className="bg-white dark:bg-slate-950 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 space-y-6 shadow-xs">
          <div className="space-y-2">
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider block">
              Frequently Asked Questions
            </span>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
              Questions About {category.name} Positions in {cityName}
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div 
                  key={idx}
                  className="rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full p-4 text-left font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white bg-slate-50/80 dark:bg-slate-900/60 hover:bg-slate-100/80 dark:hover:bg-slate-900 flex items-center justify-between transition cursor-pointer"
                  >
                    <span>{faq.question}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="p-4 bg-white dark:bg-slate-950 text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed border-t border-slate-200/60 dark:border-slate-800">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

      </div>
    </motion.div>
  );
};
